import { Box } from '@mui/material';
import { useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useBalances } from '../../queries';
import { useMarketplaceConfig } from '../../queries/marketplace';
import { WALLET_TYPE, type WalletType } from '../../types/enums';
import BonusIcon from '../../assets/icons/bonus-icon.svg';
import { formatValue } from '../../helpers';
import { formattedAmountStyles } from '../../styles/components/formattedAmount';
import { FormattedAmount, type FormattedAmountProps } from './FormattedAmount';

export type { FormattedAmountLayout as FormattedBalanceLayout } from '../../styles/components/formattedAmount';

type FormattedBalanceProps = Omit<FormattedAmountProps, 'value' | 'unit' | 'label'> & {
  balanceType?: WalletType;
  balance?: number | null;
  renderValue?: (value: number, format: (value: number) => string) => ReactNode;
};

const FormattedBalanceContent = ({ balanceType, balance, renderValue, ...props }: FormattedBalanceProps) => {
  const { i18n } = useTranslation();
  const { isStandaloneCasino } = useMarketplaceConfig();
  const amount = typeof balance === 'number' && Number.isFinite(balance) ? balance : 0;
  const showBonusIcon = balanceType === WALLET_TYPE.BONUS && isStandaloneCasino;
  const label = showBonusIcon ? (
    <Box component='img' src={BonusIcon} sx={formattedAmountStyles.bonusIcon} alt='Bonus' />
  ) : undefined;

  // CountUp needs a stable callback so formatting does not restart its animation on every render.
  const format = useCallback((value: number) => formatValue(value, i18n.language), [i18n.language]);
  const value = renderValue ? renderValue(amount, format) : format(amount);

  return <FormattedAmount value={value} label={label} {...props} />;
};

type WalletBalanceProps = Omit<FormattedBalanceProps, 'balance' | 'currency' | 'balanceType'> & {
  balanceType: WalletType;
};

// A wallet balance carries its own currency; while the wallet loads there is none to show yet.
const WalletBalance = ({ balanceType, ...props }: WalletBalanceProps) => {
  const { mainBalance, bonusBalance } = useBalances();
  const wallet = balanceType === WALLET_TYPE.MAIN ? mainBalance : bonusBalance;

  return (
    <FormattedBalanceContent
      balanceType={balanceType}
      balance={wallet?.balance}
      currency={wallet?.currency}
      {...props}
    />
  );
};

// An explicit amount (a jackpot, a stake) is in the marketplace currency unless the caller passes one.
const ExplicitBalance = ({ currency, ...props }: FormattedBalanceProps) => {
  const { data: marketplaceConfig } = useMarketplaceConfig();

  return <FormattedBalanceContent currency={currency ?? marketplaceConfig?.marketplaceConfig.currency} {...props} />;
};

/** Resolves and formats a two-decimal wallet balance, then delegates its display to FormattedAmount. */
export const FormattedBalance = ({
  balanceType = WALLET_TYPE.MAIN,
  balance,
  currency,
  ...props
}: FormattedBalanceProps) =>
  balance === undefined ? (
    <WalletBalance balanceType={balanceType} {...props} />
  ) : (
    <ExplicitBalance balanceType={balanceType} balance={balance} currency={currency} {...props} />
  );
